import React, { useState, useEffect } from 'react';
import ApiClient from '../api/client';
import { AlertIcon } from './icons';

interface MauticFieldMappingEditorProps {
  formId: number;
  apiClient: ApiClient;
  hasMauticChannels: boolean;
  mauticChannelId?: number;
  onMappingUpdated?: () => void;
}

interface MauticField {
  alias: string;
  label: string;
  type: string;
  group: string;
}

const MauticFieldMappingEditor: React.FC<MauticFieldMappingEditorProps> = ({
  formId,
  apiClient,
  hasMauticChannels,
  mauticChannelId,
  onMappingUpdated,
}) => {
  const [fieldMap, setFieldMap] = useState<Record<string, string>>({});
  const [mauticFields, setMauticFields] = useState<MauticField[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [newFormField, setNewFormField] = useState('');
  const [newMauticField, setNewMauticField] = useState('');

  useEffect(() => {
    if (hasMauticChannels) {
      loadData();
    }
  }, [formId, hasMauticChannels, mauticChannelId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [mappingResult] = await Promise.all([
        apiClient.getMauticFieldMapping(formId).catch(() => ({ id: formId, mauticFieldMap: null })),
      ]);

      setFieldMap(mappingResult.mauticFieldMap || {});

      // Load Mautic fields from the channel if we have one
      if (mauticChannelId) {
        try {
          const fields = await apiClient.getMauticFields(mauticChannelId);
          setMauticFields(fields);
        } catch {
          // Fields discovery failed — user can still type aliases manually
        }
      }

      setError(null);
    } catch (err) {
      setFieldMap({});
      setError(null);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (formField: string, mauticField: string) => {
    if (mauticField === '') {
      const updated = { ...fieldMap };
      delete updated[formField];
      setFieldMap(updated);
    } else {
      setFieldMap({ ...fieldMap, [formField]: mauticField });
    }
    setIsDirty(true);
  };

  const handleRemoveField = (formField: string) => {
    const updated = { ...fieldMap };
    delete updated[formField];
    setFieldMap(updated);
    setIsDirty(true);
  };

  const handleAddField = () => {
    if (!newFormField.trim() || !newMauticField.trim()) {
      setError('Please enter both form field name and Mautic field');
      return;
    }
    if (fieldMap[newFormField.trim()]) {
      setError('This form field is already mapped');
      return;
    }

    setFieldMap({ ...fieldMap, [newFormField.trim()]: newMauticField.trim() });
    setNewFormField('');
    setNewMauticField('');
    setIsDirty(true);
    setError(null);
  };

  const handleSave = async () => {
    // Check that email is mapped somewhere
    const mauticValues = Object.values(fieldMap);
    if (!mauticValues.includes('email')) {
      setError('At least one form field must be mapped to the Mautic "email" field');
      return;
    }

    setSaving(true);
    try {
      await apiClient.setMauticFieldMapping(formId, fieldMap);
      setIsDirty(false);
      setError(null);
      onMappingUpdated?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save field mapping');
    } finally {
      setSaving(false);
    }
  };

  if (!hasMauticChannels) {
    return null;
  }

  if (loading) {
    return (
      <div className="details-section">
        <h4>Mautic Field Mapping</h4>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <div className="loading-spinner"></div>
          <p>Loading field mapping...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="details-section">
      <h4>Mautic Field Mapping</h4>
      {error && (
        <div className="alert alert-error">
          <AlertIcon size={15} />
          <span>{error}</span>
        </div>
      )}

      <div className="editor-intro">
        <p>
          Map your form field names to Mautic contact field aliases. Only mapped fields are sent to Mautic.
        </p>
        <p>
          At minimum, one field must map to <code className="inline-code">email</code>.
        </p>
      </div>

      {Object.keys(fieldMap).length > 0 && (
        <div className="form-group">
          <label>Current Mappings</label>
          <div className="mapping-list">
            {Object.entries(fieldMap).map(([formField, mauticField]) => (
              <div key={formField} className="mapping-row">
                <input
                  type="text"
                  value={formField}
                  disabled
                  className="mono-input mapping-input"
                  title="Form field name"
                />
                <span className="mapping-arrow" aria-hidden="true">&rarr;</span>
                {mauticFields.length > 0 ? (
                  <select
                    value={mauticField}
                    onChange={(e) => handleFieldChange(formField, e.target.value)}
                    disabled={saving}
                    className="mono-input mapping-input"
                  >
                    <option value="">-- Remove --</option>
                    {mauticFields.map((f) => (
                      <option key={f.alias} value={f.alias}>
                        {f.label} ({f.alias})
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={mauticField}
                    onChange={(e) => handleFieldChange(formField, e.target.value)}
                    disabled={saving}
                    className="mono-input mapping-input"
                    title="Mautic field alias"
                  />
                )}
                <button
                  type="button"
                  onClick={() => handleRemoveField(formField)}
                  disabled={saving}
                  className="icon-btn intent-danger"
                  aria-label="Remove mapping"
                  title="Remove mapping"
                >
                  &#x2715;
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="form-group">
        <label>Add Field Mapping</label>
        <div className="mapping-row mapping-add-row">
          <input
            type="text"
            value={newFormField}
            onChange={(e) => setNewFormField(e.target.value)}
            placeholder="Form field (e.g., name)"
            disabled={saving}
            className="mono-input mapping-input"
          />
          <span className="mapping-arrow" aria-hidden="true">&rarr;</span>
          {mauticFields.length > 0 ? (
            <select
              value={newMauticField}
              onChange={(e) => setNewMauticField(e.target.value)}
              disabled={saving}
              className="mono-input mapping-input"
            >
              <option value="">-- Select Mautic field --</option>
              {mauticFields.map((f) => (
                <option key={f.alias} value={f.alias}>
                  {f.label} ({f.alias})
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              value={newMauticField}
              onChange={(e) => setNewMauticField(e.target.value)}
              placeholder="Mautic field (e.g., firstname)"
              disabled={saving}
              className="mono-input mapping-input"
            />
          )}
        </div>
        <button
          type="button"
          onClick={handleAddField}
          disabled={saving}
          className="btn btn-sm btn-secondary"
        >
          + Add Field
        </button>
      </div>

      {isDirty && (
        <div className="editor-actions">
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn btn-primary editor-save"
          >
            {saving ? 'Saving...' : 'Save Mapping'}
          </button>
          <button
            onClick={loadData}
            disabled={saving}
            className="btn btn-secondary"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default MauticFieldMappingEditor;
